import { type PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { FeatureFlagContext } from "./feature-flag-context";
import {
  applyFlagOverrides,
  type FlagName,
  type FlagOverrides,
  readFlagOverrides,
  writeFlagOverrides,
} from "./feature-flag-overrides";
import { useGetFeatureFlags } from "./query";

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const scope = JSON.stringify([user?.id ?? null, businessId ?? null]);
  const { data: featureFlags, isLoading, isError } = useGetFeatureFlags();
  const [byScope, setByScope] = useState<Record<string, FlagOverrides>>({});
  const overrides = useMemo(
    () =>
      import.meta.env.DEV ? (byScope[scope] ?? readFlagOverrides(scope)) : {},
    [byScope, scope],
  );

  const setOverride = useCallback(
    (name: FlagName, enabled: boolean | null) => {
      if (!import.meta.env.DEV) return;
      setByScope((current) => {
        const next = { ...(current[scope] ?? readFlagOverrides(scope)) };
        if (enabled === null || featureFlags?.[name]?.enabled === enabled) {
          delete next[name];
        } else {
          next[name] = enabled;
        }
        writeFlagOverrides(scope, next);
        return { ...current, [scope]: next };
      });
    },
    [featureFlags, scope],
  );

  const clearOverrides = useCallback(() => {
    if (!import.meta.env.DEV) return;
    setByScope((current) => ({ ...current, [scope]: {} }));
    writeFlagOverrides(scope, {});
  }, [scope]);

  const value = useMemo(
    () => ({
      serverFlags: featureFlags,
      featureFlags: import.meta.env.DEV
        ? applyFlagOverrides(featureFlags, overrides)
        : featureFlags,
      isLoading,
      isError,
      overrides: import.meta.env.DEV ? overrides : {},
      setOverride,
      clearOverrides,
    }),
    [featureFlags, isLoading, isError, overrides, setOverride, clearOverrides],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
